import { Controller } from "@hotwired/stimulus"

// Share a product.
// - Touch devices with the Web Share API (e.g. mobile / installed PWA) get the
//   native share sheet — the richest, most familiar experience there.
// - Desktop (where Web Share is missing or inconsistent, e.g. Firefox) gets an
//   explicit social menu: Facebook / X / WhatsApp / Email / Copy link. The menu
//   is appended to <body> so it is not clipped by the card image's
//   overflow-hidden container.
export default class extends Controller {
  static values = {
    url: String,
    title: String,
    copyLabel: String,
    copiedLabel: String,
    emailLabel: String,
  }

  async share(event) {
    event.preventDefault()
    event.stopPropagation()

    const isTouch =
      window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0

    if (navigator.share && isTouch) {
      try {
        await navigator.share({ title: this.titleValue, url: this.urlValue })
      } catch (_) {
        // user cancelled — no-op
      }
      return
    }

    this.toggleMenu(event.currentTarget)
  }

  toggleMenu(button) {
    if (this.menu) {
      this.closeMenu()
      return
    }

    const url = encodeURIComponent(this.urlValue)
    const text = encodeURIComponent(this.titleValue)
    const targets = [
      { label: "Facebook", icon: "fab fa-facebook-f", href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
      { label: "X", icon: "fab fa-x-twitter", href: `https://twitter.com/intent/tweet?url=${url}&text=${text}` },
      { label: "WhatsApp", icon: "fab fa-whatsapp", href: `https://wa.me/?text=${text}%20${url}` },
      { label: this.emailLabelValue, icon: "fas fa-envelope", href: `mailto:?subject=${text}&body=${url}` },
    ]

    const menu = document.createElement("div")
    menu.className =
      "fixed z-[200] min-w-44 bg-white rounded-lg shadow-xl border border-gray-200 py-1 text-sm text-text-primary"

    targets.forEach((t) => {
      const a = document.createElement("a")
      a.href = t.href
      a.target = "_blank"
      a.rel = "noopener noreferrer"
      a.className = "flex items-center gap-2.5 px-3 py-2 hover:bg-bg-cream"
      const icon = document.createElement("i")
      icon.className = `${t.icon} w-4 text-center`
      const span = document.createElement("span")
      span.textContent = t.label
      a.append(icon, span)
      a.addEventListener("click", () => this.closeMenu())
      menu.appendChild(a)
    })

    // Copy link
    const copy = document.createElement("button")
    copy.type = "button"
    copy.className = "w-full flex items-center gap-2.5 px-3 py-2 hover:bg-bg-cream text-left"
    const copyIcon = document.createElement("i")
    copyIcon.className = "fas fa-link w-4 text-center"
    const copyLabel = document.createElement("span")
    copyLabel.textContent = this.copyLabelValue
    copy.append(copyIcon, copyLabel)
    copy.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(this.urlValue)
        copyLabel.textContent = this.copiedLabelValue
        setTimeout(() => this.closeMenu(), 900)
      } catch (_) {
        this.closeMenu()
      }
    })
    menu.appendChild(copy)

    document.body.appendChild(menu)
    this.menu = menu
    this.position(button)

    this.outsideHandler = (e) => {
      if (this.menu && !this.menu.contains(e.target)) this.closeMenu()
    }
    this.keyHandler = (e) => {
      if (e.key === "Escape") this.closeMenu()
    }
    this.repositionHandler = () => this.position(button)

    // Defer listener attachment so the opening click doesn't immediately close it.
    setTimeout(() => {
      document.addEventListener("click", this.outsideHandler)
      document.addEventListener("keydown", this.keyHandler)
      window.addEventListener("resize", this.repositionHandler)
      window.addEventListener("scroll", this.repositionHandler, true)
    }, 0)
  }

  position(button) {
    if (!this.menu) return
    const rect = button.getBoundingClientRect()
    const menuRect = this.menu.getBoundingClientRect()
    const margin = 8

    let left = rect.right - menuRect.width
    left = Math.max(margin, Math.min(left, window.innerWidth - menuRect.width - margin))

    let top = rect.bottom + 6
    if (top + menuRect.height > window.innerHeight - margin) {
      top = rect.top - menuRect.height - 6
    }

    this.menu.style.left = `${left}px`
    this.menu.style.top = `${top}px`
  }

  closeMenu() {
    if (this.menu) {
      this.menu.remove()
      this.menu = null
    }
    if (this.outsideHandler) document.removeEventListener("click", this.outsideHandler)
    if (this.keyHandler) document.removeEventListener("keydown", this.keyHandler)
    if (this.repositionHandler) {
      window.removeEventListener("resize", this.repositionHandler)
      window.removeEventListener("scroll", this.repositionHandler, true)
    }
  }

  disconnect() {
    this.closeMenu()
  }
}
